import { View, StyleSheet, Platform, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import SidebarHeader from './SidebarHeader';
import { useCurrentRoute } from '@/hooks/useCurrentRoute';
import { useTheme } from '@/context/ThemeContext';
import { useRouter } from 'expo-router';
import { useAuthStore, useModalStore, useChatStore, useOnboardingStore, useUIStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import SidebarListItemLayout from './SidebarListItemLayout';
import PropTypes from 'prop-types';
import { Link } from 'expo-router';
import SidebarSavedAgentsSection from './SidebarSavedAgentsSection';
import SidebarSavedModelsSection from './SidebarSavedModelsSection';
import SidebarChatsSection from './SidebarChatsSection';
import SidebarCollectionsSection from './SidebarCollectionsSection';
import SidebarToolsNavSection from './SidebarToolsNavSection';
import SidebarGetAppDropdown from './SidebarGetAppDropdown';
import SidebarRemoteLink from './SidebarRemoteLink';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import {
  DEFAULT_SIDEBAR_WIDTH_MIN,
  DEFAULT_SIDEBAR_WIDTH_MAX,
  AGENT_ICON,
} from '@/constants/config';
import Icon from '@/components/Icon';
import SidebarAccountButton from '@/components/Sidebar/SidebarAccountButton';
import BannerPlacement from '@/components/Banner/BannerPlacement';
import { useTooltipAnchor } from '@/hooks/useTooltipAnchor';
import SidebarNewBadge from '@/components/Sidebar/SidebarNewBadge';
import { isSidebarItemNew } from '@/constants/sidebarNewBadges';
import { openNewChatComposer } from '@/lib/openHomeComposer';
// Create styles outside component
const createStyles = (colors, safeAreaTop, safeAreaBottom, isXS) => StyleSheet.create({
  container: {
    zIndex: 9999,
    height: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    borderRightWidth: 1,
    borderRightColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    paddingTop: safeAreaTop,
    paddingBottom: safeAreaBottom,
  },
  listContainer: {
    flex: 1,
    paddingLeft: 8,
    paddingRight: 8,
    paddingBottom: 20,
    // The SidebarHeader already reserves space for itself,
    // so this margin is just visual breathing space between the header and
    // the first list item.
    marginTop: 8,
    ...(Platform.OS === 'web' ? { overflow: 'auto', scrollbarWidth: 'thin' } : { overflow: 'hidden' }),
    backgroundColor: colors.solid_color_block,
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    // Keep the resize target entirely outside the scrolling sidebar. A
    // straddling hit target covers Firefox's thin scrollbar on Linux.
    right: -8,
    width: 8,
    height: '100%',
    cursor: 'ew-resize',
    zIndex: 2,
  },
  resizeHandleHovered: {
    backgroundColor: 'rgba(0,0,0,0.1)',
  },
  resizeHandleActive: {
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
});

const lowerSectionStyles = (colors) => StyleSheet.create({
  lowerSection: {
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
});

const proSectionStyles = (colors) => StyleSheet.create({
  proSection: {
    paddingHorizontal: 15,
    paddingBottom: 10,
    paddingTop: 10,
    marginBottom: Platform.OS === 'web' ? 0 : 15,
  },
  proTitle: {
    marginRight: 4,
    marginBottom: 10,
  },
  proDescription: {
    marginBottom: 14,
    marginTop: 0,
  },
});

const helpLinksStyles = StyleSheet.create({
  // Rendered inline inside the logged-out nav list (which already provides the
  // 8px horizontal gutter), so no extra horizontal padding here — just a small
  // top gap to separate it from "Create Music".
  container: {
    paddingTop: 6,
    paddingBottom: 10,
  },
});

// Logged-out footer links shown inline beneath "Create Music" in the nav list.
const SidebarHelpLinks = () => {
  const { t } = useTranslation();

  return (
    <View style={helpLinksStyles.container}>
      <SidebarGetAppDropdown />
      <Link href="/docs" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="help-documentation"
          title={t('sidebar.apiDocumentation')}
          icon="book-open"
          noEllipsis
        />
      </Link>
    </View>
  );
};

const SidebarAuthUpsellSection = ({ isXS }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => proSectionStyles(colors), [colors]);
  const { openModal } = useModalStore(state => state.actions);
  const router = useRouter();
  const searchQuery = useChatStore(state => state.searchQuery);

  const handleSignupClick = useCallback(() => {
    if (Platform.OS === 'web' && !isXS) {
      openModal('SignupModal');
    } else {
      router.push('/signup');
    }
  }, [openModal, router, isXS]);

  const handleLoginClick = useCallback(() => {
    if (Platform.OS === 'web' && !isXS) {
      openModal('LoginModal');
    } else {
      router.push('/login');
    }
  }, [openModal, router, isXS]);

  if (!!searchQuery && isXS) return null;

  return (
    <View style={styles.proSection}>
      <Text size="m" weight="bold" style={styles.proTitle}>
        {t('sidebar.upsellTitle')}
      </Text>
      <Text size="s" color="secondary" style={styles.proDescription}>
        {t('sidebar.upsellBody')}
      </Text>
      <View style={{ flexDirection: 'column', gap: 8 }}>
        <Button
          text={t('sidebar.logIn')}
          variant="secondary"
          size="medium"
          onPress={handleLoginClick}
        />
        <Button
          text={t('common.signUp')}
          variant="plus"
          size="medium"
          onPress={handleSignupClick}
        />
      </View>
    </View>
  );
};

const SidebarLowerSection = ({
  hasUser,
  isXS,
  isSearching,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => lowerSectionStyles(colors), [colors]);

  return (
    <View style={styles.lowerSection}>
      <BannerPlacement placement="sidebar" isCompact style={{ paddingHorizontal: 10, paddingTop: 10 }} />
      {!hasUser && <SidebarAuthUpsellSection isXS={isXS} />}
      {hasUser && !isSearching && <SidebarAccountButton />}
    </View>
  );
};

export function AppSidebar({ width, onResize, onToggle, pwaSafeAreaTop = 0 }) {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const [isResizing, setIsResizing] = useState(false);
  const { colors } = useTheme();
  const route = useCurrentRoute();
  const router = useRouter();
  const { isXS } = useResponsiveness();
  const { openModal } = useModalStore(state => state.actions);
  const searchQuery = useChatStore(state => state.searchQuery);
  const isSearching = !!searchQuery;

  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  const { registerTarget, unregisterTarget } = useOnboardingStore(state => state.actions);
  const collapsedSections = useUIStore(state => state.collapsedSections);
  const toggleSection = useUIStore(state => state.toggleSection);
  const handleToggleMore = useCallback(() => toggleSection('sidebarMore'), [toggleSection]);
  const filesItemRef = React.useRef(null);
  const newChatAnchorRef = useTooltipAnchor('new-chat');
  const memoryNavAnchorRef = useTooltipAnchor('memory-nav');
  const filesNavAnchorRef = useTooltipAnchor('files-nav');
  const modelsNavAnchorRef = useTooltipAnchor('models-nav');
  const agentsNavAnchorRef = useTooltipAnchor('agents-nav');
  const tasksNavAnchorRef = useTooltipAnchor('tasks-nav');
  const toolsNavAnchorRef = useTooltipAnchor('tools-nav');
  // const workflowsNavAnchorRef = useTooltipAnchor('workflows-nav');
  const imagesNavAnchorRef = useTooltipAnchor('images-nav');

  useEffect(() => {
    if (filesItemRef.current) {
      registerTarget(4, filesItemRef);
    }
    return () => unregisterTarget(4);
  }, [registerTarget, unregisterTarget]);

  // Memoize styles - apply PWA safe area for web when provided
  const safeAreaTop = Platform.select({ ios: insets.top, android: insets.top, default: pwaSafeAreaTop });
  const safeAreaBottom = Platform.select({ ios: insets.bottom, android: insets.bottom, default: 0 });
  const styles = useMemo(() => createStyles(colors, safeAreaTop, safeAreaBottom, isXS), [colors, safeAreaTop, safeAreaBottom, isXS]);

  const isMoreExpanded = !collapsedSections.sidebarMore;
  // Items only get the nested indent when they live under the collapsible "More"
  // group (signed-in users). Logged-out visitors render them flat.
  const moreIndentation = hasUser ? 1 : undefined;

  const moreChevron = useMemo(() => (
    <Icon
      name={isMoreExpanded ? 'chevron-down' : 'chevron-right'}
      size={12}
      color={colors.text_color_secondary}
    />
  ), [isMoreExpanded, colors.text_color_secondary]);

  const handleNewChatPress = useCallback(() => {
    openNewChatComposer(router);
  }, [router]);

  const showExploreNew = isSidebarItemNew('explore');
  const showBookmarksNew = isSidebarItemNew('bookmarks');
  const showToolsNew = isSidebarItemNew('tools');
  const showPromptsNew = isSidebarItemNew('prompts');
  // const showWorkflowsNew = isSidebarItemNew('workflows');
  const newBadge = useMemo(
    () => <SidebarNewBadge colors={colors} label={t('common.new')} />,
    [colors, t],
  );

  const staticNavItems = useMemo(() => (
    <>
      <View ref={(node) => { newChatAnchorRef.current = node; }}>
        <SidebarListItemLayout
          id="new-chat"
          title={t('nav.routes.chat.newChat')}
          icon="pen-to-square"
          isActive={route.isHome}
          onPress={handleNewChatPress}
          noEllipsis
        />
      </View>

      <View ref={(node) => { filesItemRef.current = node; filesNavAnchorRef.current = node; }}>
        <Link href="/files" asChild withAnchor={true}>
          <SidebarListItemLayout
            id="files"
            title={t('navigation.files')}
            icon='files'
            isActive={route.isFiles}
            noEllipsis
          />
        </Link>
      </View>

      <View ref={(node) => { agentsNavAnchorRef.current = node; }}>
        <Link href="/agents" asChild withAnchor={true}>
          <SidebarListItemLayout
            id="agents"
            title={t('navigation.agents')}
            icon={AGENT_ICON}
            isActive={route.isAgents}
            noEllipsis
          />
        </Link>
      </View>

      {/* <View ref={(node) => { workflowsNavAnchorRef.current = node; }}>
        <Link href="/workflows" asChild withAnchor={true}>
          <SidebarListItemLayout
            id="workflows"
            title={t('navigation.workflows') || 'Workflows'}
            icon="diagram-project"
            isActive={route.isWorkflows}
            noEllipsis
            rightIcon={showWorkflowsNew ? newBadge : undefined}
            isRightContainerVisible={showWorkflowsNew}
          />
        </Link>
      </View> */}

      <SidebarRemoteLink pathname={route.pathname} />

      {/* Logged-out visitors see every destination flat (no "More" group); signed-in
          users keep the collapsible "More" section to reduce clutter. */}
      {hasUser && (
        <SidebarListItemLayout
          id="more"
          title={t('navigation.more')}
          icon="grid-2"
          onPress={handleToggleMore}
          rightIcon={moreChevron}
          noEllipsis
        />
      )}

      {(!hasUser || isMoreExpanded) && (
        <>
          <SidebarRemoteLink placement="more" pathname={route.pathname} indentation={moreIndentation} />
          <View ref={(node) => { memoryNavAnchorRef.current = node; }}>
            <Link href="/memory" asChild withAnchor={true}>
              <SidebarListItemLayout
                id="memory"
                title={t('navigation.memory')}
                icon='brain'
                isActive={route.isMemory}
                noEllipsis
                indentation={moreIndentation}
              />
            </Link>
          </View>

          <View ref={(node) => { tasksNavAnchorRef.current = node; }}>
            <Link href="/tasks" asChild withAnchor={true}>
              <SidebarListItemLayout
                id="tasks"
                title={t('navigation.tasks')}
                icon='clipboard-list'
                isActive={route.isTasks}
                noEllipsis
                indentation={moreIndentation}
              />
            </Link>
          </View>

          <View ref={(node) => { toolsNavAnchorRef.current = node; }}>
            <Link href="/tools" asChild withAnchor={true}>
              <SidebarListItemLayout
                id="tools"
                title={t('navigation.tools') || 'Tools'}
                icon='wand-magic-sparkles'
                isActive={route.isTools}
                noEllipsis
                indentation={moreIndentation}
                rightIcon={showToolsNew ? newBadge : undefined}
                isRightContainerVisible={showToolsNew}
              />
            </Link>
          </View>

          <View ref={(node) => { modelsNavAnchorRef.current = node; }}>
            <Link href="/models" asChild withAnchor={true}>
              <SidebarListItemLayout
                id="models"
                title={t('navigation.models')}
                icon="cube"
                isActive={route.isModels}
                noEllipsis
                indentation={moreIndentation}
              />
            </Link>
          </View>

          <Link href="/bookmarks" asChild withAnchor={true}>
            <SidebarListItemLayout
              id="bookmarks"
              title={t('navigation.bookmarks')}
              icon='bookmark'
              isActive={route.isBookmarks}
              noEllipsis
              indentation={moreIndentation}
              rightIcon={showBookmarksNew ? newBadge : undefined}
              isRightContainerVisible={showBookmarksNew}
            />
          </Link>

        </>
      )}

    </>
  ), [t, route.isHome, route.isAgents, route.isModels, route.isModel, route.isFiles, route.isBookmarks, route.isMemory, route.isTasks, route.isCoworker, route.isTools, route.isWorkflows, route.pathname, isXS, hasUser, moreIndentation, isMoreExpanded, handleToggleMore, handleNewChatPress, moreChevron, showBookmarksNew, showToolsNew, newBadge, colors.text_color_secondary]);

  // Logged-out visitors get a flat, curated catalog-first ordering. Models is a
  // plain link to the catalog; only Tools expands into a "featured + see all" list.
  const loggedOutNavItems = useMemo(() => (
    <>
      <SidebarListItemLayout
        id="new-chat"
        title={t('nav.routes.chat.newChat')}
        icon="pen-to-square"
        isActive={route.isHome}
        onPress={handleNewChatPress}
        noEllipsis
      />

      <Link href="/explore" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="explore"
          title={t('navigation.explore')}
          icon="compass"
          isActive={route.isExplore}
          noEllipsis
          rightIcon={showExploreNew ? newBadge : undefined}
          isRightContainerVisible={showExploreNew}
        />
      </Link>

      <Link href="/models" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="models"
          title={t('navigation.models')}
          icon="cube"
          isActive={route.isModels}
          noEllipsis
        />
      </Link>

      <SidebarToolsNavSection />

      <Link href="/agents" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="agents"
          title={t('navigation.agents')}
          icon={AGENT_ICON}
          isActive={route.isAgents}
          noEllipsis
        />
      </Link>

      <Link href="/tasks" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="tasks"
          title={t('navigation.tasks')}
          icon='clipboard-list'
          isActive={route.isTasks}
          noEllipsis
        />
      </Link>

      <Link href="/prompts" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="prompts"
          title={t('navigation.prompts') || 'Prompt Library'}
          icon="layer-group"
          isActive={route.isPrompts}
          noEllipsis
          rightIcon={showPromptsNew ? newBadge : undefined}
          isRightContainerVisible={showPromptsNew}
        />
      </Link>


      <Link href="/files" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="files"
          title={t('navigation.files')}
          icon='files'
          isActive={route.isFiles}
          noEllipsis
        />
      </Link>

      <Link href="/memory" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="memory"
          title={t('navigation.memory')}
          icon='brain'
          isActive={route.isMemory}
          noEllipsis
        />
      </Link>

      <View ref={(node) => { imagesNavAnchorRef.current = node; }}>
        <Link href="/images" asChild withAnchor={true}>
          <SidebarListItemLayout
            id="images"
            title={t('navigation.createImages')}
            icon='image'
            isActive={route.isImages}
            noEllipsis
          />
        </Link>
      </View>

      <Link href="/videos" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="videos"
          title={t('navigation.createVideos')}
          icon='video'
          isActive={route.isVideos}
          noEllipsis
        />
      </Link>

      <Link href="/music" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="music"
          title={t('navigation.createMusic')}
          icon='music'
          isActive={route.isMusic}
          noEllipsis
        />
      </Link>

      {/* Get-the-app + Documentation / Terms / Privacy live inline at the bottom
          of the logged-out nav list (no longer in the lower footer section). */}
      <SidebarHelpLinks />
    </>
  ), [t, route.isHome, route.isExplore, route.isPrompts, route.isAgents, route.isTasks, route.isCoworker, route.isFiles, route.isMemory, route.isImages, route.isVideos, route.isMusic, isXS, handleNewChatPress, showExploreNew, showPromptsNew, newBadge]);

  // Memoized handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    
    let rafId;
    const handleMouseMove = (moveEvent) => {
      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      // Schedule the update on the next animation frame
      rafId = requestAnimationFrame(() => {
        const newWidth = Math.min(Math.max(moveEvent.clientX, DEFAULT_SIDEBAR_WIDTH_MIN), DEFAULT_SIDEBAR_WIDTH_MAX);
        onResize(newWidth, true);
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      const finalWidth = Math.min(Math.max(e.clientX, DEFAULT_SIDEBAR_WIDTH_MIN), DEFAULT_SIDEBAR_WIDTH_MAX);
      onResize(finalWidth, false);
      if (Platform.OS === 'web') {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }
    };

    if (Platform.OS === 'web') {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  }, [onResize]);

  // Memoize resize handle
  const resizeHandle = useMemo(() => {
    if (Platform.OS !== 'web' || isXS) return null;
    
    return (
      <Pressable 
        style={({ hovered }) => [
          styles.resizeHandle,
          hovered && styles.resizeHandleHovered,
          isResizing && styles.resizeHandleActive
        ]}
        onMouseDown={handleMouseDown}
      />
    );
  }, [isXS, styles, isResizing, handleMouseDown]);

  return (
    <View style={[styles.container, { width }]}>
      {/* Chat search is an account-only feature — hide the search icon for logged-out visitors. */}
      <SidebarHeader withSearch={hasUser} onToggle={onToggle} />
      
      <View style={styles.listContainer}>
        {hasUser ? staticNavItems : loggedOutNavItems}
        {hasUser && <SidebarSavedModelsSection />}
        {!isSearching && hasUser && <SidebarSavedAgentsSection />}
        {!isSearching && hasUser && <SidebarCollectionsSection /> }
        {hasUser && <SidebarChatsSection />}
      </View>

      <SidebarLowerSection
        hasUser={hasUser}
        isXS={isXS}
        isSearching={isSearching}
      />

      {resizeHandle}
    </View>
  );
}

AppSidebar.propTypes = {
  width: PropTypes.number,
  onResize: PropTypes.func,
  onToggle: PropTypes.func,
  pwaSafeAreaTop: PropTypes.number,
};

AppSidebar.whyDidYouRender = {
  logOnDifferentValues: true,
  customName: 'AppSidebar',
  logOwnerReasons: true
};

export default React.memo(AppSidebar);
